import { activateManagedScripts, emptyPreferences, getVisitorId, googleState, grantedTypes, hasCategoryConsent, queuePending, readStoredConsent, retryPending, saveStoredConsent, setGoogleDefaults, updateGoogleConsent } from './consent'
import { chooseLanguage, ConsentModal } from './modal'
import { createUuid } from './uuid'
import type { CMPEventMap, CMPPublicApi, ColorMode, ConsentAction, ConsentPreferences, ConsentState, FlatTranslations, InitOptions, ModalPresentation, ModalTheme, PublicConfiguration, RuntimeOptions } from './types'

export * from './types'

const SDK_VERSION = '1.0.0'
setGoogleDefaults()

let options: InitOptions | null = null
let configuration: PublicConfiguration | null = null
let state: ConsentState | null = null
let modal: ConsentModal | null = null
let previewModal: ConsentModal | null = null
let initialization: Promise<ConsentState | null> | null = null
let scriptObserverCleanup: (() => void) | null = null
const listeners = new Map<keyof CMPEventMap, Set<(payload: never) => void>>()
const reportedErrors = new WeakSet<Error>()
let resolveReady!: (value: ConsentState | null) => void
let rejectReady!: (reason: unknown) => void
const ready = new Promise<ConsentState | null>((resolve, reject) => { resolveReady = resolve; rejectReady = reject })

const api: CMPPublicApi = {
  ready,
  renderPreview: (...args) => withUiRecovery(() => renderPreview(...args)),
  init: (value) => withAsyncUiRecovery(() => init(value)),
  update: (next) => withUiRecovery(() => updateOptions(next)),
  openPreferences: () => withUiRecovery(() => modal?.openPreferences()),
  getConsent: () => withUiRecovery(() => state ? structuredClone(state) : null),
  hasConsent: (category) => hasCategoryConsent(state, category),
  updateConsent: (preferences) => withAsyncUiRecovery(() => {
    requireInitialized()
    const next = { ...(state?.preferences ?? emptyPreferences()), ...preferences }
    return recordDecision('save_preferences', next, state?.language ?? undefined)
  }),
  acceptAll: () => withAsyncUiRecovery(() => recordDecision('accept_all', allPreferences(), state?.language)),
  rejectAll: () => withAsyncUiRecovery(() => recordDecision('reject_all', emptyPreferences(), state?.language)),
  withdrawAll: () => withAsyncUiRecovery(() => recordDecision('withdrawn', emptyPreferences(), state?.language)),
  on(event, listener) {
    const eventListeners = listeners.get(event) ?? new Set()
    eventListeners.add(listener as (payload: never) => void)
    listeners.set(event, eventListeners)
    return () => eventListeners.delete(listener as (payload: never) => void)
  },
}

window.CMP = api

async function init(initOptions: InitOptions) {
  if (initialization) {
    if (options?.projectKey !== initOptions.projectKey) throw new Error('CMP is already initialized for another project')
    return initialization
  }
  options = normalizeOptions(initOptions)
  initialization = initialize()
  return initialization
}

function updateOptions(next: RuntimeOptions) {
  requireInitialized()
  modal!.updateOptions(normalizeRuntimeOptions(next))
}

async function initialize() {
  try {
    configuration = await fetchConfiguration(options!)
    await domReady()
    modal = new ConsentModal(configuration, options!, recordDecision, handleRuntimeError)
    modal.mount()
    void retryPending(options!.projectKey)
    window.addEventListener('online', () => void retryPending(options!.projectKey), { passive: true })

    const stored = readStoredConsent(options!.projectKey, configuration.policyVersion)
    if (stored) {
      state = stored
      applyConsentState(stored)
      modal.setSavedState(stored)
    } else if ((options!.displayMode ?? configuration.project.policy.modalPresentation?.displayMode) === 'auto-accept') {
      const accepted = await recordDecision('auto_accept', allPreferences(), chooseLanguage(configuration, options!.language))
      modal.setSavedState(accepted)
    } else {
      modal.openInitial()
    }
    emit('ready', state)
    resolveReady(state)
    return state
  } catch (error) {
    configuration = null
    const resolved = handleRuntimeError(error, '[CMP] Unable to initialize consent management:')
    rejectReady(resolved)
    throw resolved
  }
}

async function fetchConfiguration(initOptions: InitOptions) {
  const url = `${initOptions.apiBaseUrl}/api/sdk/projects/${encodeURIComponent(initOptions.projectKey)}/config`
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(8_000) })
  let body: { data?: PublicConfiguration; error?: { message?: string } } | null
  try {
    body = await response.json()
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    throw new Error(`Invalid privacy settings response (HTTP ${response.status}): ${reason}`)
  }
  if (!response.ok || !body?.data) {
    throw new Error(`${body?.error?.message ?? 'Privacy settings are unavailable'} (HTTP ${response.status})`)
  }
  return body.data
}

async function recordDecision(
  action: ConsentAction,
  preferences: ConsentPreferences,
  requestedLanguage?: string,
) {
  requireInitialized()
  const language = requestedLanguage && configuration!.project.policy.availableLanguages.includes(requestedLanguage)
    ? requestedLanguage
    : configuration!.project.policy.availableLanguages[0] ?? 'en'
  const googleConsent = googleState(configuration!, preferences)
  const eventId = createUuid()
  const visitorId = state?.visitorId ?? getVisitorId(options!.projectKey)
  const consentUrl = `${options!.apiBaseUrl}/api/sdk/projects/${encodeURIComponent(options!.projectKey)}/consents`
  const body = {
    eventId,
    visitorId,
    configurationId: configuration!.configurationId,
    policyVersion: configuration!.policyVersion,
    presentationVersion: configuration!.presentationVersion,
    sdkVersion: SDK_VERSION,
    action,
    language,
    grantedConsentTypes: grantedTypes(googleConsent),
  }

  const privacySafe = body.grantedConsentTypes.every((type) => type === 'security_storage')
  const createState = (recordedAt: string, expiresAt: string): ConsentState => ({
    eventId,
    visitorId,
    policyVersion: configuration!.policyVersion,
    presentationVersion: configuration!.presentationVersion,
    language,
    preferences: { ...preferences },
    googleConsent,
    recordedAt,
    expiresAt,
  })
  const tentative = createState(
    new Date().toISOString(),
    new Date(Date.now() + configuration!.consentMaxAgeSeconds * 1_000).toISOString(),
  )

  if (privacySafe) {
    state = tentative
    saveStoredConsent(options!.projectKey, tentative)
    applyConsentState(tentative)
    emit('change', tentative)
  }

  let receipt: { recordedAt: string; expiresAt: string } | undefined
  try {
    const response = await fetch(consentUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(8_000),
    })
    const result = await response.json() as {
      data?: { eventId: string; recordedAt: string; expiresAt: string }
      error?: { message?: string }
    }
    if (!response.ok || !result.data) throw new Error(result.error?.message ?? 'Unable to record your privacy choice')
    receipt = result.data
  } catch (error) {
    if (!privacySafe) throw error
    queuePending(options!.projectKey, { url: consentUrl, body })
    return tentative
  }

  const confirmed = createState(receipt.recordedAt, receipt.expiresAt)
  state = confirmed
  saveStoredConsent(options!.projectKey, confirmed)
  if (!privacySafe) {
    applyConsentState(confirmed)
    emit('change', confirmed)
  }
  return confirmed
}

function applyConsentState(nextState: ConsentState) {
  updateGoogleConsent(nextState.googleConsent)
  scriptObserverCleanup?.()
  scriptObserverCleanup = activateManagedScripts(nextState, options?.cspNonce)
}

function emit<K extends keyof CMPEventMap>(event: K, payload: CMPEventMap[K]) {
  for (const listener of listeners.get(event) ?? []) {
    try { listener(payload as never) } catch (error) {
      if (event === 'error') console.error('[CMP] Error listener failed:', error)
      else handleRuntimeError(error)
    }
  }
  window.dispatchEvent(new CustomEvent(`cmp:${event}`, { detail: payload }))
}

function handleRuntimeError(error: unknown, message = '[CMP] Unable to display consent management:') {
  const resolved = error instanceof Error ? error : new Error(String(error))
  const instances = [modal, previewModal]
  modal = null
  previewModal = null
  for (const instance of instances) {
    try { instance?.destroy() } catch (cleanupError) { console.error('[CMP] Unable to clean up consent UI:', cleanupError) }
  }
  if (reportedErrors.has(resolved)) return resolved
  reportedErrors.add(resolved)
  console.error(message, resolved)
  emit('error', resolved)
  return resolved
}

function withUiRecovery<T>(operation: () => T): T {
  try { return operation() } catch (error) { throw handleRuntimeError(error) }
}

async function withAsyncUiRecovery<T>(operation: () => Promise<T>): Promise<T> {
  try { return await operation() } catch (error) { throw handleRuntimeError(error) }
}

function requireInitialized() {
  if (!options || !configuration || !modal) throw new Error('CMP is not initialized yet')
}

function normalizeOptions(value: InitOptions): InitOptions {
  if (!value.projectKey.trim()) throw new Error('projectKey is required')
  const apiBaseUrl = value.apiBaseUrl.replace(/\/+$/, '')
  new URL(apiBaseUrl)
  return {
    ...value,
    projectKey: value.projectKey.trim(),
    apiBaseUrl,
    colorMode: value.colorMode ?? 'auto',
  }
}

function normalizeRuntimeOptions(value: RuntimeOptions): RuntimeOptions {
  if (value.colorMode !== undefined && !['auto', 'light', 'dark'].includes(value.colorMode)) {
    throw new Error('colorMode must be auto, light, or dark')
  }
  if (value.displayMode !== undefined && !['modal', 'banner', 'auto-accept'].includes(value.displayMode)) {
    throw new Error('displayMode must be modal, banner, or auto-accept')
  }
  if (value.settingsPosition !== undefined && !['none', 'bottom-left', 'bottom-right'].includes(value.settingsPosition)) {
    throw new Error('settingsPosition must be none, bottom-left, or bottom-right')
  }
  return { ...value }
}

function domReady() {
  if (document.body) return Promise.resolve()
  return new Promise<void>((resolve) => document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }))
}

function allPreferences(): ConsentPreferences {
  return { ads: true, analytics: true, functionality: true, personalization: true }
}

function renderPreview(
  theme: ModalTheme,
  colorMode: ColorMode = 'auto',
  presentation: ModalPresentation = { displayMode: 'modal', settingsButtonPosition: 'bottom-right' },
  translations: FlatTranslations = {},
) {
  previewModal?.destroy()
  previewModal = null
  document.querySelectorAll('cmp-consent-root').forEach((node) => node.remove())
  const previewConfiguration: PublicConfiguration = {
    configurationId: createUuid(),
    policyVersion: 'preview',
    presentationVersion: 'preview',
    consentMaxAgeSeconds: 180 * 86_400,
    sdk: { version: SDK_VERSION, scriptUrl: '', manifestUrl: '' },
    project: {
      name: 'Preview',
      publicKey: 'cmp_pk_preview',
      policy: {
        id: createUuid(),
        availableLanguages: ['en'],
        googleConsentTypes: ['ad_storage', 'ad_user_data', 'ad_personalization', 'analytics_storage', 'functionality_storage', 'personalization_storage', 'security_storage'],
        privacyPolicyUrl: '#',
        translations: { en: translations },
        modalTheme: theme,
        modalPresentation: presentation,
      },
    },
  }
  const previewOptions: InitOptions = {
    projectKey: 'cmp_pk_preview',
    apiBaseUrl: location.origin,
    colorMode,
    settingsButton: false,
  }
  previewModal = new ConsentModal(previewConfiguration, previewOptions, async (action, preferences, language) => {
    const now = new Date().toISOString()
    return {
      eventId: createUuid(),
      visitorId: createUuid(),
      policyVersion: 'preview',
      presentationVersion: 'preview',
      language,
      preferences,
      googleConsent: googleState(previewConfiguration, preferences),
      recordedAt: now,
      expiresAt: now,
    }
  }, handleRuntimeError)
  previewModal.mount()
  if (presentation.displayMode === 'auto-accept') previewModal.openPreferences()
  else previewModal.openInitial()
}

function autoInitialize() {
  const script = document.currentScript as HTMLScriptElement | null
  const projectKey = script?.dataset.cmpProjectKey
  if (!script || !projectKey) return
  const apiBaseUrl = script.dataset.cmpApiBase ?? new URL(script.src, document.baseURI).origin
  void init({
    projectKey,
    apiBaseUrl,
    language: script.dataset.cmpLanguage,
    colorMode: parseColorMode(script.dataset.cmpColorMode),
    cspNonce: (script.dataset.cmpCspNonce ?? script.nonce) || undefined,
    displayMode: parseDisplayMode(script.dataset.cmpDisplayMode),
    settingsButton: parseBooleanOverride(script.dataset.cmpSettingsButton),
    settingsPosition: parseSettingsPosition(script.dataset.cmpSettingsPosition),
  }).catch(() => undefined)
}

function parseColorMode(value?: string) {
  return value === 'light' || value === 'dark' ? value : 'auto'
}

function parseDisplayMode(value?: string) {
  return value === 'modal' || value === 'banner' || value === 'auto-accept' ? value : undefined
}

function parseSettingsPosition(value?: string) {
  return value === 'none' || value === 'bottom-left' || value === 'bottom-right' ? value : undefined
}

function parseBooleanOverride(value?: string) {
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

autoInitialize()
